import { useCallback, useEffect, useRef, useState } from 'react';
import { planEventsApi } from '../api/planEvents';
import { defensePlansApi } from '../api/defensePlans';
import { defensePlanPartiesApi } from '../api/defensePlanParties';
import { DEFENSE_STRATEGIES } from '../components/map/defenseStrategies';
import { partyDisplayName } from '../components/map/planParties';
import { computeFeatureLabels } from '../utils/planFeatureLabels';
import { orderFeaturesNewestFirst } from '../utils/planBundles';
import { createPrefixedLogger } from '../utils/logger';
import type { PlanEvent } from '../types';

const log = createPrefixedLogger('usePublicPlanEvent');

export type PublicPlanEvent =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error' }
  | {
      status: 'ready';
      event: PlanEvent;
      /**
       * The bundle's defenses by strategy label ("Fire break 1"), when the
       * linked plan is readable. Null when the plan is private, gone, or the
       * bundle no longer exists: the page then shows no work list.
       */
      work: string[] | null;
      /** The plan to link to, only when it is readable. */
      readablePlanId: string | null;
      /** The bundle leader's public name, when there is one. Never contacts. */
      leaderName: string | null;
    };

interface Related {
  work: string[] | null;
  readablePlanId: string | null;
  leaderName: string | null;
}

const NO_RELATED: Related = { work: null, readablePlanId: null, leaderName: null };

/** The plan and leader behind an event's one link. Any denial or absence reads as "show less". */
async function loadRelated(planId: string, bundleId: string): Promise<Related> {
  let plan;
  try {
    plan = await defensePlansApi.getPlan(planId);
  } catch (error) {
    // A private plan is denied by design.
    if ((error as { code?: string })?.code !== 'permission-denied') {
      log.warn('Could not load the event plan:', error);
    }
    return NO_RELATED;
  }
  if (!plan) return NO_RELATED;
  const bundle = plan.bundles.find((b) => b.id === bundleId);
  const labels = computeFeatureLabels(plan.features, (s) => DEFENSE_STRATEGIES[s].label);
  const members = bundle
    ? orderFeaturesNewestFirst(plan.features.filter((f) => f.bundleId === bundleId)).reverse()
    : [];
  let leaderName: string | null = null;
  if (bundle?.leaderPartyId) {
    try {
      const party = await defensePlanPartiesApi.getParty(planId, bundle.leaderPartyId);
      leaderName = party ? partyDisplayName(party) || null : null;
    } catch (error) {
      log.warn('Could not load the bundle leader:', error);
    }
  }
  return {
    work: bundle ? members.map((f) => labels[f.id] ?? DEFENSE_STRATEGIES[f.strategy].label) : null,
    readablePlanId: plan.id,
    leaderName,
  };
}

/**
 * Everything the public /event page shows, for a visitor with no account.
 * The event itself is live (a new sign-up or a cancel shows without a
 * reload); the plan, its work list and the leader are read once per link.
 */
export function usePublicPlanEvent(eventId: string | undefined): {
  state: PublicPlanEvent;
  /** Folds a sign-up's returned headcount into the page before the listener catches up. */
  noteGoingCount: (goingCount: number) => void;
} {
  const [state, setState] = useState<PublicPlanEvent>({ status: 'loading' });
  const relatedKey = useRef<string | null>(null);
  const related = useRef<Related>(NO_RELATED);

  useEffect(() => {
    if (!eventId) {
      setState({ status: 'missing' });
      return;
    }
    setState({ status: 'loading' });
    relatedKey.current = null;
    let cancelled = false;
    const unsubscribe = planEventsApi.subscribeEvent(
      eventId,
      (event) => {
        if (cancelled) return;
        if (!event) {
          setState({ status: 'missing' });
          return;
        }
        const link = event.links[0];
        const key = `${link.planId}/${link.bundleId}`;
        const show = () => setState({ status: 'ready', event, ...related.current });
        if (relatedKey.current === key) {
          show();
          return;
        }
        relatedKey.current = key;
        void loadRelated(link.planId, link.bundleId).then((loaded) => {
          if (cancelled || relatedKey.current !== key) return;
          related.current = loaded;
          show();
        });
      },
      (error) => {
        if (cancelled) return;
        // A missing doc can read as permission-denied before the rules see it.
        const code = (error as { code?: string })?.code;
        setState({ status: code === 'permission-denied' ? 'missing' : 'error' });
      }
    );
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [eventId]);

  const noteGoingCount = useCallback((goingCount: number) => {
    setState((prev) =>
      prev.status === 'ready' && prev.event.goingCount < goingCount
        ? { ...prev, event: { ...prev.event, goingCount } }
        : prev
    );
  }, []);

  return { state, noteGoingCount };
}
